import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useLeadSources } from '@/routes/leadSources/useLeadSources';

/**
 * How this record was attributed, shown and changed in place (M1-45).
 *
 * <b>M1-28's missing half.</b> That story shipped attribution end to end on the
 * API and built the catalog screen to manage the vocabulary, but nothing to see
 * or change an attribution once it existed: `AddCustomerDialog` wrote one at
 * creation and no screen ever read it back. A customer attributed to the wrong
 * source could only be fixed by archiving and recreating them, which destroys
 * the job history hanging off the record.
 *
 * ⚠️ <b>One component, two callers.</b> Customers and jobs ask the same
 * question through two same-shaped endpoints, and two hand-copied screens would
 * drift — this repo has been bitten twice already by a local retyping of
 * somebody else's shape. The caller supplies the record's current values and a
 * save; everything about how the question is asked lives here.
 */
export function LeadSourceField({
  leadSourceId,
  leadSourceLabel,
  leadSourceDetail,
  canManage,
  onSave,
  onSaved,
}: {
  leadSourceId: string | null | undefined;
  /** Denormalized onto the record by the API, which is what makes a RETIRED source displayable. */
  leadSourceLabel: string | null | undefined;
  leadSourceDetail: string | null | undefined;
  canManage: boolean;
  onSave: (leadSourceId: string | null, leadSourceDetail: string | null) => Promise<unknown>;
  /** The page owns the live region; this reports the new label, or null for cleared. */
  onSaved: (label: string | null) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const [detail, setDetail] = useState('');

  // Only while the picker is open: the catalog is a second request, and a
  // record that is merely being READ already carries the label it needs.
  const catalog = useLeadSources(false, editing);

  function open() {
    setPickedId(leadSourceId ?? '');
    setDetail(leadSourceDetail ?? '');
    setFailed(false);
    setEditing(true);
  }

  // ⚠️ Active sources, PLUS this record's own even when it has been retired.
  // The server refuses a retired source as a CHANGE and permits it as a
  // RETENTION (LeadSourceEndpoints.ResolveAsync), so a picker built from the
  // active list alone would show a blank current value on exactly those rows —
  // and saving an unrelated detail edit would quietly clear the attribution.
  // The label comes off the record, so this costs no extra request.
  const options = (catalog.data ?? []).map((source) => ({
    id: source.id,
    label: source.label,
    isRetired: false,
  }));
  if (leadSourceId && leadSourceLabel && !options.some((option) => option.id === leadSourceId)) {
    options.push({ id: leadSourceId, label: leadSourceLabel, isRetired: true });
  }

  async function save() {
    setSaving(true);
    setFailed(false);
    try {
      // Detail without a source is a 400 the API states plainly ("Pick a lead
      // source before adding detail about it"), so clearing the source clears
      // the detail with it rather than sending a pair the server will refuse.
      const nextId = pickedId === '' ? null : pickedId;
      const nextDetail = nextId === null ? null : detail.trim() || null;
      await onSave(nextId, nextDetail);
      setEditing(false);
      onSaved(options.find((option) => option.id === nextId)?.label ?? null);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="lead-source">
        <p className="text-sm">
          {leadSourceLabel ? (
            <>
              <span className="font-medium">{leadSourceLabel}</span>
              {leadSourceDetail ? (
                <span className="text-[var(--color-muted-foreground)]"> — {leadSourceDetail}</span>
              ) : null}
            </>
          ) : (
            // Words, not an empty cell. A blank here reads as "still loading"
            // and is indistinguishable from a field nobody has filled in.
            <span className="text-[var(--color-muted-foreground)]">
              {t('leadSourceField.none')}
            </span>
          )}
        </p>
        {canManage ? (
          <Button type="button" variant="outline" size="sm" onClick={open}>
            {leadSourceLabel ? t('leadSourceField.change') : t('leadSourceField.record')}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="lead-source-editor">
      <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('leadSourceField.source')}</span>
          <select
            className="w-full rounded-md border px-3 py-2"
            value={pickedId}
            disabled={saving}
            // ⚠️ The typed detail is KEPT when the source goes back to "not
            // recorded", not wiped. Wiping it made an accidental click on the
            // first option destroy what somebody had just typed, and — the
            // reason this changed — it also made the save-time guard below
            // unreachable: a mutation that removed that guard survived the whole
            // suite, because the onChange had already emptied the field. Two
            // mechanisms, one of them never exercised. Now the guard is the only
            // one, and it is tested.
            onChange={(event) => setPickedId(event.target.value)}
          >
            <option value="">{t('leadSourceField.unset')}</option>
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.isRetired
                  ? t('leadSourceField.retiredOption', { label: option.label })
                  : option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('leadSourceField.detail')}</span>
          <input
            className="w-full rounded-md border px-3 py-2"
            value={detail}
            // Disabled rather than hidden, as in AddCustomerDialog: a field that
            // appears and vanishes moves everything under it, and the reason it
            // cannot be filled is worth showing.
            disabled={saving || pickedId === ''}
            placeholder={t('leadSourceField.detailPlaceholder')}
            onChange={(event) => setDetail(event.target.value)}
          />
        </label>
      </div>

      {failed ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('leadSourceField.failed')}
        </p>
      ) : null}

      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={() => void save()} disabled={saving}>
          {saving ? t('leadSourceField.saving') : t('common.save')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={saving}
          onClick={() => setEditing(false)}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
